/**
 * Sync Queue Manager with Exponential Backoff
 * Handles background sync of offline operations
 */

import { indexedDBService, SyncQueueItem } from './indexedDB';
import { toast } from 'sonner';
import api from '../firebase/api';
import { getCompanyId } from '../tenantContext';

export type SyncEventListener = (event: {
  type: 'sync-start' | 'sync-complete' | 'sync-error' | 'sync-progress';
  data?: any;
}) => void;

class SyncQueueManager {
  private isSyncing = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private listeners: SyncEventListener[] = [];
  private maxRetries = 5;
  private baseDelay = 1000; // 1 second

  constructor() {
    // Listen for online events and trigger immediate sync
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.sync().catch(console.error);
      });

      window.addEventListener('offline', () => {
      });

      // Also sync every 30 seconds if online and have pending items
      setInterval(async () => {
        if (navigator.onLine && !this.isSyncing) {
          const status = await this.getQueueStatus();
          if (status.pending > 0) {
            this.sync().catch(console.error);
          }
        }
      }, 30000); // Every 30 seconds
    }
  }

  addEventListener(listener: SyncEventListener): void {
    this.listeners.push(listener);
  }

  removeEventListener(listener: SyncEventListener): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private emit(event: { type: 'sync-start' | 'sync-complete' | 'sync-error' | 'sync-progress'; data?: any }): void {
    this.listeners.forEach(listener => listener(event));
  }

  async addToQueue(
    type: 'CREATE' | 'UPDATE' | 'DELETE',
    entity: 'well' | 'entry' | 'message' | 'task' | 'note',
    data: any
  ): Promise<string> {
    const id = await indexedDBService.addToSyncQueue({ type, entity, data });

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.sync().catch(console.error);
    }

    return id;
  }

  async sync(): Promise<void> {
    if (this.isSyncing) {
      return;
    }

    if (!navigator.onLine) {
      return;
    }

    // Check if IndexedDB is initialized
    if (!indexedDBService.isInitialized()) {
      return;
    }

    this.isSyncing = true;
    this.emit({ type: 'sync-start' });

    try {
      const pendingItems = await indexedDBService.getPendingSyncItems();

      if (pendingItems.length === 0) {
        this.emit({ type: 'sync-complete', data: { synced: 0, failed: 0 } });
        return;
      }


      let synced = 0;
      let failed = 0;

      for (const item of pendingItems) {
        try {
          await this.processSyncItem(item);

          // Mark as success so it can be cleared
          await indexedDBService.updateSyncItem(item.id, { status: 'success' });

          synced++;
          this.emit({ type: 'sync-progress', data: { synced, total: pendingItems.length } });
        } catch (error) {
          console.error(`Failed to sync item ${item.id}:`, error);
          failed++;

          // Update retry count and status
          const newRetryCount = item.retryCount + 1;

          if (newRetryCount >= this.maxRetries) {
            // Max retries reached - mark as failed permanently
            await indexedDBService.updateSyncItem(item.id, {
              status: 'failed',
              retryCount: newRetryCount,
              lastError: error instanceof Error ? error.message : String(error),
            });
          } else {
            // Still have retries left - mark as pending with increased retry count
            await indexedDBService.updateSyncItem(item.id, {
              status: 'pending',
              retryCount: newRetryCount,
              lastError: error instanceof Error ? error.message : String(error),
            });
          }
        }
      }

      this.emit({ type: 'sync-complete', data: { synced, failed } });

      // Clean up successful items
      await indexedDBService.clearSuccessfulSyncItems();

      // Show toast notification
      if (synced > 0) {
        toast.success(`Synced ${synced} ${synced === 1 ? 'item' : 'items'}`);
      }
      if (failed > 0) {
        toast.error(`Failed to sync ${failed} ${failed === 1 ? 'item' : 'items'}`);
      }

    } catch (error) {
      console.error('Sync error:', error);
      this.emit({ type: 'sync-error', data: error });
      toast.error('Sync failed');
    } finally {
      this.isSyncing = false;
    }
  }

  private async syncItem(item: SyncQueueItem): Promise<void> {
    // Mark as syncing
    await indexedDBService.updateSyncItem(item.id, { status: 'syncing' });

    // Calculate backoff delay
    const delay = this.calculateBackoff(item.retryCount);
    if (delay > 0) {
      await this.sleep(delay);
    }

    // Process the item
    await this.processSyncItem(item);

    // Mark as success
    await indexedDBService.updateSyncItem(item.id, { status: 'success' });
  }

  private calculateBackoff(retryCount: number): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    return Math.min(this.baseDelay * Math.pow(2, retryCount), 30000); // Max 30 seconds
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async processSyncItem(item: SyncQueueItem): Promise<void> {
    const companyId = getCompanyId();
    if (!companyId) {
      throw new Error('No tenant ID found - cannot sync');
    }


    switch (item.entity) {
      case 'well':
        if (item.type === 'CREATE') {
          await api.createWell(companyId, item.data);
        } else if (item.type === 'UPDATE') {
          await api.updateWell(item.data.id, item.data);
        } else if (item.type === 'DELETE') {
          await api.deleteWell(item.data.id);
        }
        break;

      case 'entry':
        if (item.type === 'CREATE') {
          // Use upsert to handle potential duplicates from offline retries
          if (item.data.wellId && item.data.entryDate) {
            await api.upsertWellEntry(companyId, item.data.wellId, item.data.entryDate, item.data);
          } else {
            await api.createWellEntry(companyId, item.data);
          }
        } else if (item.type === 'UPDATE') {
          await api.updateProductionEntry(item.data.id, item.data);
        } else if (item.type === 'DELETE') {
          await api.deleteProductionEntry(item.data.id);
        }
        break;

      case 'task':
        if (item.type === 'CREATE') {
          await api.createTask(companyId, item.data);
        } else if (item.type === 'UPDATE') {
          await api.updateTask(item.data.id, item.data);
        } else if (item.type === 'DELETE') {
          await api.deleteTask(item.data.id);
        }
        break;

      case 'message':
        // Assuming message maps to notifications for now, or log warning
        // If it's a notification:
        // await api.createNotification(companyId, item.data);
        break;

      case 'note':
        break;

      case 'draft_entry':
        if (item.type === 'UPDATE_DRAFT_FIELD') {
          // item.data has draftId, wellId, fieldName, value, companyId, userId
          // IMPORTANT: Firestore rules require companyId, userId, wellId on create/update
          await api.updateDraftEntryField(
            item.data.companyId,
            item.data.wellId,
            item.data.draftId,
            {
              fields: { [item.data.fieldName]: item.data.value },
              lastModifiedAt: new Date().toISOString(),
              // Required by Firestore security rules for create/update validation
              companyId: item.data.companyId,
              wellId: item.data.wellId,
              userId: item.data.userId,
            }
          );
        } else if (item.type === 'DELETE') {
          await api.deleteDraftEntry(
            item.data.companyId,
            item.data.wellId,
            item.data.draftId
          );
        }
        break;

      default:
        break;
    }
  }

  // REMOVED: Auto-sync with timer - Now using event-driven sync
  // startAutoSync(intervalMs: number = 30000): void {
  //   console.log('⚠️ Timer-based sync disabled - using event-driven sync instead');
  // }

  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async getQueueStatus(): Promise<{
    pending: number;
    syncing: number;
    failed: number;
    total: number;
  }> {
    try {
      // Check if IndexedDB is initialized
      if (!indexedDBService.isInitialized()) {
        return { pending: 0, syncing: 0, failed: 0, total: 0 };
      }

      const items = await indexedDBService.getSyncQueue();

      return {
        pending: items.filter(i => i.status === 'pending').length,
        syncing: items.filter(i => i.status === 'syncing').length,
        failed: items.filter(i => i.status === 'failed').length,
        total: items.length,
      };
    } catch (error) {
      console.error('Error getting queue status:', error);
      return { pending: 0, syncing: 0, failed: 0, total: 0 };
    }
  }

  async retryFailedItems(): Promise<void> {
    try {
      // Check if IndexedDB is initialized
      if (!indexedDBService.isInitialized()) {
        toast.warning('Database not ready yet. Please wait a moment.');
        return;
      }

      const items = await indexedDBService.getSyncQueue();
      const failedItems = items.filter(i => i.status === 'failed');

      for (const item of failedItems) {
        await indexedDBService.updateSyncItem(item.id, {
          status: 'pending',
          retryCount: 0,
          lastError: undefined,
        });
      }

      toast.info(`Retrying ${failedItems.length} failed ${failedItems.length === 1 ? 'item' : 'items'}`);

      if (navigator.onLine) {
        await this.sync();
      }
    } catch (error) {
      console.error('Error retrying failed items:', error);
      toast.error('Failed to retry items');
    }
  }

  async clearQueue(): Promise<void> {
    try {
      // Check if IndexedDB is initialized
      if (!indexedDBService.isInitialized()) {
        toast.warning('Database not ready yet. Please wait a moment.');
        return;
      }

      const items = await indexedDBService.getSyncQueue();
      for (const item of items) {
        await indexedDBService.removeSyncItem(item.id);
      }
      toast.success('Sync queue cleared');
    } catch (error) {
      console.error('Error clearing queue:', error);
      toast.error('Failed to clear queue');
    }
  }

  /**
   * Get all pending items from the sync queue
   */
  async getPendingItems(): Promise<SyncQueueItem[]> {
    try {
      if (!indexedDBService.isInitialized()) {
        return [];
      }
      return await indexedDBService.getPendingSyncItems();
    } catch (error) {
      console.error('Error getting pending items:', error);
      return [];
    }
  }

  /**
   * Get count of pending items (lighter weight than getPendingItems)
   */
  async getPendingCount(): Promise<number> {
    try {
      if (!indexedDBService.isInitialized()) {
        return 0;
      }
      const status = await this.getQueueStatus();
      return status.pending;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }
}

// Singleton instance
export const syncQueueManager = new SyncQueueManager();